/**
 * DecisionFlow — ImportDataPanel
 * ui/src/components/admin/ImportDataPanel.jsx
 * S554 — Import Data / Addin parity
 * S558 D1 — Column mapping step added to EntityUploadPanel
 *
 * Route: /admin/import  (SETUP › Import Data in CompAdminShell)
 *
 * Tab structure (mirrors addin workbench):
 *   Positions | OTE History | RT Shells | Rate Table Rows | Ramp Schedules |
 *   Plans | Plan Assignments | Quotas | Credits | AOP Inputs | Import Log
 *
 * Upload flow per entity (four-step post S558):
 *   1. Choose file  — file picker, filename display
 *   2. Map columns  — fetch saved mapping, confirm/correct source→target per column
 *   3. Validate     — canonical header check against FIELD_CONTRACTS
 *   4. Import       — POST /api/v1/import/upload, result display
 *
 * Column mapping (S558 D1):
 *   GET  /api/v1/import/column-mapping?source_system=direct_api&file_type=X
 *        → [{ column_mapping_id, source_column_name, target_column_name, is_required, is_active }]
 *   POST /api/v1/import/column-mapping
 *        → upsert mapping set for (source_system, file_type)
 *   ensureColumnMapping() retired — mapping is persisted before upload; no silent
 *   POST on import. Mapping is applied client-side (source col renamed to target col)
 *   before the CSV is forwarded to the server.
 *
 * Two-actor approval flows:
 *   RT Shells:
 *     Pending:  GET  /api/v1/addin/lookups/pending_rate_table_approvals
 *     Approve:  POST /api/v1/rate-tables/{rate_table_id}/approve  (per shell)
 *     Error:    SELF_APPROVAL_PROHIBITED
 *
 *   Plans:
 *     Pending:  GET  /api/v1/addin/lookups/pending_plan_approvals
 *     Approve:  POST /api/v1/plans/import/approve  (by plan_import_session_id)
 *     Errors:   SELF_APPROVAL_PROHIBITED | PLAN_ALREADY_APPROVED | PLAN_SESSION_NOT_FOUND
 *     After upload: plan_import_session_id displayed — Actor 1 shares with Actor 2
 *
 * OTE History — per-employee atomicity (S555 D2): 200 | 207 partial | 422
 * All others  — 200 | 207 partial | 422 all-rejected
 *
 * Future OIs:
 *   OI-WEBAPP-AGENT-IMPORT         — guided mode (Guide me + stub wired)
 *   OI-SETUP-EXECUTE-CONSOLIDATION — collapse Setup/Execute at ~80% parity
 *   OI-ADDIN-RT-PLAN-TAB-GROUPING  — align addin to webapp sub-tab grouping
 *
 * Controls (non-negotiable):
 *   CSS: rgb(var(--color-*)) only
 *   Tables: max-h-* + overflow-y-auto + sticky thead
 *   Error envelope: json?.error?.code / json?.detail?.error?.code
 *   No P4-xx codes on any user-facing surface
 */

import { useCallback, useState, useEffect, useRef } from "react";
import {
  AlertTriangle, CheckCircle2, XCircle, Info,
  Sparkles, Clock, RotateCcw, FileText, Lock,
} from "lucide-react";
import ImportLogViewer from "../tenant-admin/ImportLogViewer.jsx";

// ── Constants ────────────────────────────────────────────────────────────────

const SOURCE_SYSTEM = "direct_api";

const TABS = [
  { id: "positions",      label: "Positions"       },
  { id: "ote_history",    label: "OTE History"     },
  { id: "rate_tables",    label: "RT Shells",      subTabs: [
    { id: "rate_table_shell", label: "RT Shells"        },
    { id: "rate_table_row",   label: "Rate Table Rows"  },
  ]},
  { id: "ramp_schedules", label: "Ramp Schedules" },
  { id: "plans_group",    label: "Plans",           subTabs: [
    { id: "plan",            label: "Plans"             },
    { id: "plan_assignment", label: "Plan Assignments" },
  ]},
  { id: "quotas",         label: "Quotas"          },
  { id: "credits",        label: "Credits"         },
  { id: "aop_inputs",     label: "AOP Inputs" },
  { id: "import_log",     label: "Import Log",      log: true },
];

// Field contracts — sourced from import_service.py _REQUIRED_TARGETS
const FIELD_CONTRACTS = {
  position: {
    required: ["employee_id", "full_name", "role_code", "segment_code", "hire_date", "is_active"],
    optional: ["ramp_start_date", "manager_employee_id", "territory_code", "calculation_mode"],
    notes: [
      "is_active: true / 1 / yes = active — anything else = inactive",
      "hire_date: ISO format YYYY-MM-DD",
      "employee_id is the identity anchor — used for dedup and FK resolution",
    ],
  },
  ote_history: {
    required: ["employee_id", "ote_amount", "ote_currency_code", "effective_date", "end_date"],
    optional: [],
    notes: [
      "All-or-nothing batch — no partial accept. Fix all errors before re-submitting.",
      "ote_amount: decimal string — never a float (e.g. \"125000.00\")",
      "effective_date / end_date: ISO format YYYY-MM-DD",
      "Overlapping date ranges for the same employee are rejected (OTE_OVERLAP_CONFLICT)",
      "end_date is required by router even though nullable in schema",
    ],
  },
  rate_table_shell: {
    required: ["rate_table_code", "rate_table_name", "plan_code", "role_code", "segment_code", "default_ceiling_pct", "attainment_ceiling_pct", "effective_date"],
    optional: [],
    notes: [
      "Shells are created unapproved — use the Approve tab after import",
      "default_ceiling_pct: DDL CHECK >= 0.85 AND <= 1.0",
      "attainment_ceiling_pct: DDL CHECK > 1.0",
      "effective_date: ISO format YYYY-MM-DD",
    ],
  },
  rate_table_row: {
    required: ["rate_table_code", "from_pct", "to_pct", "payout_pct", "interpolation"],
    optional: [],
    notes: [
      "Rate table shell must be approved before rows are accepted",
      "interpolation: linear | step",
      "to_pct must be greater than from_pct per row",
      "band_sequence and is_accelerator are server-derived — do not include",
    ],
  },
  plan: {
    required: [
      "plan_code", "plan_name", "role_code", "segment_code", "fiscal_period_code",
      "ote_min", "ote_midpoint", "ote_max", "ote_currency_code",
      "base_pct", "variable_pct",
      "arr_weight", "mbo_weight", "nps_weight", "adoption_weight",
      "effective_date",
    ],
    optional: ["rollup_mode", "business_unit_code"],
    notes: [
      "Plans imported as draft — a second comp admin must approve before assignments can be loaded",
      "rollup_mode: leave blank for IC / AE / CSM / SE roles (server sets NULL)",
      "base_pct + variable_pct must sum to 1.0",
      "arr_weight + mbo_weight + nps_weight + adoption_weight must sum to 1.0",
      "effective_date: ISO format YYYY-MM-DD — controls which fiscal period the plan applies to",
      "business_unit_code: optional — leave blank for NULL-BU global plan, supply for BU-scoped plan",
    ],
  },
  quota: {
    required: ["fiscal_period_code", "quota_local", "quota_local_currency_code"],
    optional: ["employee_id", "person_uuid", "business_unit_code"],
    notes: [
      "C-IDENTITY: either employee_id OR person_uuid must be present per row",
      "quota_local: decimal string — never a float",
      "quota_reporting is server-derived — do not include",
    ],
  },
  credit: {
    required: [
      "employee_id", "source_reference_id", "selling_motion", "business_unit_code",
      "fiscal_period_code", "credit_local", "credit_local_currency_code",
    ],
    optional: ["deal_close_date"],
    notes: [
      "source_reference_id is used for dedup integrity — must be unique per credit event",
      "selling_motion: supply the motion_code (e.g. NEW_LOGO, EXPANSION)",
      "deal_close_date: optional on webapp surface — ISO format YYYY-MM-DD",
      "credit_local: decimal string — never a float",
      "credit_reporting and credit_reporting_currency_code are SERVER-GENERATED — do not include in CSV (D-01)",
    ],
  },
  ramp_schedule: {
    required: ["role_code", "ramp_month", "ramp_factor", "effective_date"],
    optional: [],
    notes: [
      "ramp_month: positive integer (month number from hire date, e.g. 1, 2, 3…)",
      "ramp_factor: decimal string between 0 and 1 (e.g. \"0.5000\" = 50% ramp)",
      "effective_date: ISO format YYYY-MM-DD — must not be in the future",
      "Mixed role_code and effective_date values are accepted in one file",
      "Warning shown if the same role_code appears with more than one effective_date",
    ],
  },
  plan_assignment: {
    required: ["employee_id", "plan_code", "effective_date", "fiscal_period_code", "business_unit_code"],
    optional: [],
    notes: [
      "employee_id resolves to position_id — position must exist and be active",
      "plan_code must reference an approved plan",
      "effective_date: ISO format YYYY-MM-DD",
      "fiscal_period_code: matches period_code in the fiscal_period table",
      "business_unit_code: must match an active business unit",
    ],
  },
  aop_input: {
    required: ["role_code", "segment_code", "business_unit_code", "planned_variable_comp", "planned_headcount", "currency_code"],
    optional: [],
    notes: [
      "fiscal_period_code is file-level — supply once as a column on every row",
      "planned_variable_comp: decimal string — total variable comp budget for this role/segment/BU",
      "planned_headcount: positive integer",
      "currency_code: ISO 4217 three-letter code (e.g. USD, GBP, EUR)",
    ],
  },
};

// Column mapping definitions — fallback defaults when no saved mapping exists.
// Mirrors import_service.py _REQUIRED_TARGETS. Used to seed a first-time mapping.
const COLUMN_MAPPINGS = {
  position: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true  },
    { source_column_name: "full_name",           target_column_name: "full_name",           is_required: true  },
    { source_column_name: "role_code",           target_column_name: "role_code",           is_required: true  },
    { source_column_name: "segment_code",        target_column_name: "segment_code",        is_required: true  },
    { source_column_name: "hire_date",           target_column_name: "hire_date",           is_required: true  },
    { source_column_name: "is_active",           target_column_name: "is_active",           is_required: true  },
    { source_column_name: "ramp_start_date",     target_column_name: "ramp_start_date",     is_required: false },
    { source_column_name: "manager_employee_id", target_column_name: "manager_employee_id", is_required: false },
    { source_column_name: "territory_code",      target_column_name: "territory_code",      is_required: false },
    { source_column_name: "calculation_mode",    target_column_name: "calculation_mode",    is_required: false },
  ],
  ote_history: [
    { source_column_name: "employee_id",       target_column_name: "employee_id",       is_required: true },
    { source_column_name: "ote_amount",        target_column_name: "ote_amount",        is_required: true },
    { source_column_name: "ote_currency_code", target_column_name: "ote_currency_code", is_required: true },
    { source_column_name: "effective_date",    target_column_name: "effective_date",    is_required: true },
    { source_column_name: "end_date",          target_column_name: "end_date",          is_required: true },
  ],
  rate_table_shell: [
    { source_column_name: "rate_table_code",        target_column_name: "rate_table_code",        is_required: true },
    { source_column_name: "rate_table_name",        target_column_name: "rate_table_name",        is_required: true },
    { source_column_name: "plan_code",              target_column_name: "plan_code",              is_required: true },
    { source_column_name: "role_code",              target_column_name: "role_code",              is_required: true },
    { source_column_name: "segment_code",           target_column_name: "segment_code",           is_required: true },
    { source_column_name: "default_ceiling_pct",    target_column_name: "default_ceiling_pct",    is_required: true },
    { source_column_name: "attainment_ceiling_pct", target_column_name: "attainment_ceiling_pct", is_required: true },
    { source_column_name: "effective_date",         target_column_name: "effective_date",         is_required: true },
  ],
  rate_table_row: [
    { source_column_name: "rate_table_code", target_column_name: "rate_table_code", is_required: true },
    { source_column_name: "from_pct",        target_column_name: "from_pct",        is_required: true },
    { source_column_name: "to_pct",          target_column_name: "to_pct",          is_required: true },
    { source_column_name: "payout_pct",      target_column_name: "payout_pct",      is_required: true },
    { source_column_name: "interpolation",   target_column_name: "interpolation",   is_required: true },
  ],
  plan: [
    { source_column_name: "plan_code",          target_column_name: "plan_code",          is_required: true  },
    { source_column_name: "plan_name",          target_column_name: "plan_name",          is_required: true  },
    { source_column_name: "role_code",          target_column_name: "role_code",          is_required: true  },
    { source_column_name: "segment_code",       target_column_name: "segment_code",       is_required: true  },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true  },
    { source_column_name: "ote_min",            target_column_name: "ote_min",            is_required: true  },
    { source_column_name: "ote_midpoint",       target_column_name: "ote_midpoint",       is_required: true  },
    { source_column_name: "ote_max",            target_column_name: "ote_max",            is_required: true  },
    { source_column_name: "ote_currency_code",  target_column_name: "ote_currency_code",  is_required: true  },
    { source_column_name: "base_pct",           target_column_name: "base_pct",           is_required: true  },
    { source_column_name: "variable_pct",       target_column_name: "variable_pct",       is_required: true  },
    { source_column_name: "arr_weight",         target_column_name: "arr_weight",         is_required: true  },
    { source_column_name: "mbo_weight",         target_column_name: "mbo_weight",         is_required: true  },
    { source_column_name: "nps_weight",         target_column_name: "nps_weight",         is_required: true  },
    { source_column_name: "adoption_weight",    target_column_name: "adoption_weight",    is_required: true  },
    { source_column_name: "rollup_mode",        target_column_name: "rollup_mode",        is_required: false },
    { source_column_name: "effective_date",     target_column_name: "effective_date",     is_required: true  },
    { source_column_name: "business_unit_code", target_column_name: "business_unit_code", is_required: false },
  ],
  quota: [
    { source_column_name: "fiscal_period_code",       target_column_name: "fiscal_period_code",       is_required: true  },
    { source_column_name: "quota_local",               target_column_name: "quota_local",               is_required: true  },
    { source_column_name: "quota_local_currency_code", target_column_name: "quota_local_currency_code", is_required: true  },
    { source_column_name: "employee_id",               target_column_name: "employee_id",               is_required: false },
    { source_column_name: "person_uuid",               target_column_name: "person_uuid",               is_required: false },
    { source_column_name: "business_unit_code",        target_column_name: "business_unit_code",        is_required: false },
  ],
  credit: [
    { source_column_name: "employee_id",                    target_column_name: "employee_id",                    is_required: true  },
    { source_column_name: "source_reference_id",            target_column_name: "source_reference_id",            is_required: true  },
    { source_column_name: "selling_motion",                 target_column_name: "selling_motion",                 is_required: true  },
    { source_column_name: "business_unit_code",             target_column_name: "business_unit_code",             is_required: true  },
    { source_column_name: "fiscal_period_code",             target_column_name: "fiscal_period_code",             is_required: true  },
    { source_column_name: "credit_local",               target_column_name: "credit_local",               is_required: true  },
    { source_column_name: "credit_local_currency_code", target_column_name: "credit_local_currency_code", is_required: true  },
    { source_column_name: "deal_close_date",            target_column_name: "deal_close_date",            is_required: false },
  ],
  ramp_schedule: [
    { source_column_name: "role_code",      target_column_name: "role_code",      is_required: true },
    { source_column_name: "ramp_month",     target_column_name: "ramp_month",     is_required: true },
    { source_column_name: "ramp_factor",    target_column_name: "ramp_factor",    is_required: true },
    { source_column_name: "effective_date", target_column_name: "effective_date", is_required: true },
  ],
  plan_assignment: [
    { source_column_name: "employee_id",        target_column_name: "employee_id",        is_required: true },
    { source_column_name: "plan_code",          target_column_name: "plan_code",          is_required: true },
    { source_column_name: "effective_date",     target_column_name: "effective_date",     is_required: true },
    { source_column_name: "fiscal_period_code", target_column_name: "fiscal_period_code", is_required: true },
    { source_column_name: "business_unit_code", target_column_name: "business_unit_code", is_required: true },
  ],
  aop_input: [
    { source_column_name: "role_code",             target_column_name: "role_code",             is_required: true },
    { source_column_name: "segment_code",          target_column_name: "segment_code",          is_required: true },
    { source_column_name: "business_unit_code",    target_column_name: "business_unit_code",    is_required: true },
    { source_column_name: "planned_variable_comp", target_column_name: "planned_variable_comp", is_required: true },
    { source_column_name: "planned_headcount",     target_column_name: "planned_headcount",     is_required: true },
    { source_column_name: "currency_code",         target_column_name: "currency_code",         is_required: true },
    { source_column_name: "fiscal_period_code",    target_column_name: "fiscal_period_code",    is_required: true },
  ],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Fetch saved column mapping rows for (source_system, file_type).
 * Returns array of { column_mapping_id, source_column_name, target_column_name, is_required, is_active }.
 * Falls back to COLUMN_MAPPINGS defaults if server returns empty or errors.
 */
async function fetchColumnMapping(fileType) {
  try {
    const resp = await fetch(
      `/api/v1/import/column-mapping?source_system=${SOURCE_SYSTEM}&file_type=${fileType}`,
      { credentials: "include" },
    );
    if (!resp.ok) throw new Error("fetch failed");
    const data = await resp.json();
    const rows = Array.isArray(data) ? data : data?.mappings ?? [];
    if (rows.length > 0) {
      // Merge saved rows with COLUMN_MAPPINGS defaults.
      // Any default field missing from saved rows is appended as a new unsaved row.
      // This ensures fields added to COLUMN_MAPPINGS after initial save are surfaced.
      const savedSourceNames = new Set(rows.map((r) => r.source_column_name));
      const defaults = COLUMN_MAPPINGS[fileType] ?? [];
      const missing = defaults.filter((d) => !savedSourceNames.has(d.source_column_name));
      const appended = missing.map((m, i) => ({
        column_mapping_id: -(rows.length + i + 1), // negative sentinel — not yet persisted
        source_column_name: m.source_column_name,
        target_column_name: m.target_column_name,
        is_required: m.is_required,
        is_active: true,
      }));
      return [...rows, ...appended];
    }
  } catch { /* fall through to defaults */ }
  // No saved mapping — return defaults so the UI can seed a first-time mapping
  return (COLUMN_MAPPINGS[fileType] ?? []).map((m, i) => ({
    column_mapping_id: -(i + 1), // negative sentinel — not yet persisted
    source_column_name: m.source_column_name,
    target_column_name: m.target_column_name,
    is_required: m.is_required,
    is_active: true,
  }));
}

/**
 * Persist the confirmed mapping set for (source_system, file_type).
 * Non-blocking — called after user confirms mapping, before upload.
 */
async function saveColumnMapping(fileType, mappingRows) {
  try {
    await fetch("/api/v1/import/column-mapping", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source_system: SOURCE_SYSTEM,
        file_type: fileType,
        mappings: mappingRows.map(({ source_column_name, target_column_name, is_required }) => ({
          source_column_name,
          target_column_name,
          is_required: is_required ?? false,
        })),
      }),
    });
  } catch { /* non-blocking */ }
}

/**
 * Apply a mapping to raw CSV headers, returning canonical header names.
 * source_column_name (lowercased) → target_column_name.
 * Headers not in the mapping pass through unchanged.
 */
function applyMappingToHeaders(rawHeaders, mappingRows) {
  const lookup = {};
  for (const row of mappingRows) {
    lookup[row.source_column_name.toLowerCase()] = row.target_column_name;
  }
  return rawHeaders.map((h) => lookup[h.toLowerCase()] ?? h);
}

async function parseCsvFile(file) {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) throw new Error("File appears to be empty.");
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return { headers, dataRowCount: Math.max(0, lines.length - 1) };
}

function validateHeaders(headers, fileType) {
  const contract = FIELD_CONTRACTS[fileType];
  if (!contract) return [];
  return contract.required.filter((col) => !headers.includes(col));
}

function fmt(n) { return (n ?? 0).toLocaleString(); }

function friendlyApprovalError(code, fallback) {
  const MAP = {
    SELF_APPROVAL_PROHIBITED: "You cannot approve records you created. A second comp admin must approve.",
    PLAN_ALREADY_APPROVED:    "These plans have already been approved.",
    PLAN_SESSION_NOT_FOUND:   "Session ID not found. Verify the ID and try again.",
  };
  return MAP[code] ?? fallback ?? "Approval failed. Please try again.";
}

// ── Shared UI components ──────────────────────────────────────────────────────

function TabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div className="flex gap-0 border-b-2 border-[rgb(var(--color-border))] overflow-x-auto">
      {tabs.map(({ id, label, oi }) => (
        <button
          key={id}
          type="button"
          onClick={() => onTabChange(id)}
          className={[
            "relative whitespace-nowrap px-4 py-2.5 text-tok-sm -mb-0.5 border-b-2 transition-colors",
            activeTab === id
              ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))] font-semibold"
              : "border-transparent text-[rgb(var(--color-text-muted))] font-medium hover:text-[rgb(var(--color-text))] hover:border-[rgb(var(--color-border))]",
          ].join(" ")}
        >
          {label}
          {oi && (
            <span
              className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))] align-middle"
              title="Coming soon — use Excel Add-in"
            />
          )}
        </button>
      ))}
    </div>
  );
}

function SubTabBar({ subTabs, activeSubTab, onSubTabChange }) {
  return (
    <div className="flex gap-0 border-b border-[rgb(var(--color-border))] mb-5">
      {subTabs.map(({ id, label, oi }) => (
        <button
          key={id}
          type="button"
          onClick={() => onSubTabChange(id)}
          className={[
            "whitespace-nowrap px-4 py-2 text-tok-xs -mb-px border-b-2 transition-colors",
            activeSubTab === id
              ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))] font-semibold"
              : "border-transparent text-[rgb(var(--color-text-muted))] font-medium hover:text-[rgb(var(--color-text))]",
          ].join(" ")}
        >
          {label}
          {oi && (
            <span
              className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))] align-middle"
              title="Coming soon"
            />
          )}
        </button>
      ))}
    </div>
  );
}

/** 1 · Import | 2 · Approve panel switcher — solid filled style matching addin. */
function PanelSwitcher({ panels, activePanel, onPanelChange }) {
  return (
    <div className="flex rounded-lg border border-[rgb(var(--color-border))] overflow-hidden text-tok-xs font-medium w-fit">
      {panels.map((p, i) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onPanelChange(p.id)}
          className={[
            "px-4 py-1.5 transition-colors",
            i > 0 ? "border-l border-[rgb(var(--color-border))]" : "",
            activePanel === p.id
              ? "bg-[rgb(var(--color-primary))] text-white"
              : "bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]",
          ].join(" ")}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function FieldContractCard({ fileType }) {
  const contract = FIELD_CONTRACTS[fileType];
  if (!contract) return null;
  return (
    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-2.5">
      <div>
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">Required fields</p>
        <p className="font-mono text-tok-xs text-[rgb(var(--color-text))] leading-relaxed break-all">{contract.required.join(", ")}</p>
      </div>
      {contract.optional.length > 0 && (
        <div>
          <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide mb-1">Optional fields</p>
          <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed break-all">{contract.optional.join(", ")}</p>
        </div>
      )}
      {contract.notes.length > 0 && (
        <div className="border-t border-[rgb(var(--color-border))] pt-2.5 space-y-1">
          {contract.notes.map((note, i) => (
            <p key={i} className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
              <span className="text-[rgb(var(--color-text))]">·</span> {note}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function RejectedRowsTable({ rows }) {
  if (!rows?.length) return null;
  const visible  = rows.slice(0, 50);
  const overflow = rows.length - 50;
  return (
    <div className="space-y-1.5">
      <p className="text-tok-xs font-semibold text-[rgb(var(--color-danger))]">Rejected rows</p>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-[rgb(var(--color-danger))] border-opacity-30">
        <table className="w-full text-tok-xs">
          <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-14">Row</th>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-32">Field</th>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-44">Error code</th>
              <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Message</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}>
                <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{row.row_number ?? "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-text))]">{row.field ?? "—"}</td>
                <td className="px-3 py-1.5 font-mono text-[rgb(var(--color-danger))]">{row.error_code ?? row.code ?? "—"}</td>
                <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{row.message ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {overflow > 0 && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          and {fmt(overflow)} more — fix the CSV and re-upload to see remaining errors
        </p>
      )}
    </div>
  );
}

function ImportResultCard({ result }) {
  if (!result) return null;
  const { status, json } = result;

  if (status === 200) {
    const log = json?.import_log ?? {};
    return (
      <div className="rounded-lg border border-[rgb(var(--color-success))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">Import complete</p>
        </div>
        <div className="flex gap-5">
          {log.rows_inserted  != null && <Stat label="Inserted"  value={log.rows_inserted}  color="success" />}
          {log.rows_updated   != null && <Stat label="Updated"   value={log.rows_updated}   color="success" />}
          {log.rows_unchanged != null && <Stat label="Unchanged" value={log.rows_unchanged} />}
        </div>
      </div>
    );
  }

  if (status === 207) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0" />
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Partial import</p>
          </div>
          <div className="flex gap-5">
            {json?.rows_loaded    != null && <Stat label="Loaded"    value={json.rows_loaded}    color="success" />}
            {json?.rows_unchanged != null && <Stat label="Unchanged" value={json.rows_unchanged} />}
            {json?.rows_rejected  != null && <Stat label="Rejected"  value={json.rows_rejected}  color="danger"  />}
          </div>
          {json?.error_summary && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{json.error_summary}</p>}
        </div>
        <RejectedRowsTable rows={json?.rejected_rows} />
      </div>
    );
  }

  // 422 or network error
  const errCode  = json?.error?.code ?? json?.error ?? json?.detail?.error?.code;
  const errMsg   = json?.error?.message ?? json?.message ?? json?.detail?.error?.message;
  const rejected = json?.error?.rejected_rows ?? json?.rejected_rows ?? [];
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Import failed</p>
        </div>
        {errCode && <p className="font-mono text-tok-xs text-[rgb(var(--color-danger))]">{errCode}</p>}
        {errMsg  && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{errMsg}</p>}
        {json?.error?.rows_rejected != null && (
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {fmt(json.error.rows_rejected)} row{json.error.rows_rejected !== 1 ? "s" : ""} rejected — no data written
          </p>
        )}
      </div>
      <RejectedRowsTable rows={rejected} />
    </div>
  );
}

function Stat({ label, value, color }) {
  const cls = color === "success" ? "text-[rgb(var(--color-success))]"
    : color === "danger" ? "text-[rgb(var(--color-danger))]"
    : "text-[rgb(var(--color-text))]";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-tok-sm font-semibold tabular-nums ${cls}`}>{fmt(value)}</span>
      <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</span>
    </div>
  );
}

function ImportHistoryCard({ fileType, historyKey }) {
  const [logs, setLogs]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`/api/v1/import-logs?file_type=${fileType}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setLogs((Array.isArray(d) ? d : d?.logs ?? d?.data ?? []).slice(0, 10)); })
      .catch(() => { if (!cancelled) setError("Could not load import history."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fileType, historyKey]);

  return (
    <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]">
        <Clock className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Import history</p>
      </div>
      {loading && <p className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">Loading…</p>}
      {error   && <p className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">{error}</p>}
      {!loading && !error && !logs?.length && <p className="px-4 py-3 text-tok-xs text-[rgb(var(--color-text-muted))]">No imports yet.</p>}
      {!loading && !error && logs?.length > 0 && (
        <div className="max-h-48 overflow-y-auto">
          <table className="w-full text-tok-xs">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Date</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Loaded</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Unchanged</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Rejected</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">File</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.import_log_id ?? i} className={i % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}>
                  <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{log.created_at ? new Date(log.created_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-1.5 text-[rgb(var(--color-success))]">{fmt(log.rows_inserted ?? log.rows_loaded)}</td>
                  <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))]">{fmt(log.rows_unchanged)}</td>
                  <td className={`px-3 py-1.5 ${(log.rows_rejected ?? 0) > 0 ? "text-[rgb(var(--color-danger))]" : "text-[rgb(var(--color-text-muted))]"}`}>{(log.rows_rejected ?? 0) > 0 ? fmt(log.rows_rejected) : "—"}</td>
                  <td className="px-3 py-1.5 text-[rgb(var(--color-text-muted))] truncate max-w-[140px]" title={log.source_filename}>{log.source_filename ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OiCalloutPanel({ entityLabel, oiId, description }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{entityLabel} — webapp upload not yet available</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">{description}</p>
            <p className="font-mono text-tok-xs text-[rgb(var(--color-text-muted))]">{oiId}</p>
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-1">
        <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Workaround</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
          Open the DecisionFlow Excel Add-in → Workbench → <strong className="text-[rgb(var(--color-text))]">{entityLabel}</strong> tab.
          The same CSV schema is accepted on both surfaces.
        </p>
      </div>
    </div>
  );
}

// ── ColumnMappingStep ─────────────────────────────────────────────────────────
//
// Renders between file select and validate. Shows saved mapping rows from
// GET /api/v1/import/column-mapping, allows inline edit of source_column_name,
// and persists via POST before advancing to validate.
//
// Props:
//   fileType       {string}    e.g. "position"
//   rawHeaders     {string[]}  headers extracted from uploaded CSV (row 0)
//   mappingRows    {Array}     from fetchColumnMapping() — saved or default rows
//   onConfirm      {function}  called with confirmed mappingRows[]
//   onCancel       {function}  resets to IDLE

function ColumnMappingStep({ fileType, rawHeaders, mappingRows, onConfirm, onCancel }) {
  // Local editable copy of mapping rows: user can reassign source_column_name per row
  const [rows, setRows] = useState(() =>
    mappingRows.map((r) => ({ ...r, _edited_source: r.source_column_name }))
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Derive which required target fields are not yet covered by a mapped source
  const missingRequired = rows
    .filter((r) => r.is_required && !r._edited_source?.trim())
    .map((r) => r.target_column_name);

  const canConfirm = missingRequired.length === 0;

  const handleSourceChange = useCallback((idx, value) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, _edited_source: value } : r));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return;
    setSaving(true);
    setSaveError(null);
    const confirmed = rows.map((r) => ({
      ...r,
      source_column_name: r._edited_source?.trim() || r.source_column_name,
    }));
    await saveColumnMapping(fileType, confirmed);
    setSaving(false);
    onConfirm(confirmed);
  }, [canConfirm, rows, fileType, onConfirm]);

  // Options for the source column selector: CSV headers + blank option
  const headerOptions = ["", ...rawHeaders];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Map columns</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
          Match your CSV column headers to the required fields. Saved mappings are pre-filled —
          update if your file uses different column names.
        </p>
      </div>

      {/* Missing required callout */}
      {missingRequired.length > 0 && (
        <div className="rounded-lg border border-[rgb(var(--color-danger))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1">
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Required fields not mapped</p>
          </div>
          <p className="font-mono text-tok-xs text-[rgb(var(--color-danger))] leading-relaxed">
            {missingRequired.join(", ")}
          </p>
        </div>
      )}

      {/* Mapping table */}
      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-tok-xs">
            <thead className="sticky top-0 bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))] w-8"></th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Your CSV column</th>
                <th className="px-3 py-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">DecisionFlow field</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const isMissing = row.is_required && !row._edited_source?.trim();
                return (
                  <tr
                    key={row.target_column_name}
                    className={idx % 2 === 0 ? "bg-[rgb(var(--color-surface))]" : "bg-[rgb(var(--color-surface-muted))]"}
                  >
                    {/* Required badge */}
                    <td className="px-3 py-2 text-center">
                      {row.is_required && (
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-primary))]"
                          title="Required"
                        />
                      )}
                    </td>
                    {/* Source column selector */}
                    <td className="px-3 py-1.5">
                      <select
                        value={row._edited_source ?? ""}
                        onChange={(e) => handleSourceChange(idx, e.target.value)}
                        className={[
                          "w-full rounded border px-2 py-1 text-tok-xs bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))]",
                          "focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]",
                          isMissing
                            ? "border-[rgb(var(--color-danger))]"
                            : "border-[rgb(var(--color-border))]",
                        ].join(" ")}
                      >
                        <option value="">— not mapped —</option>
                        {headerOptions.filter(Boolean).map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </select>
                    </td>
                    {/* Target field (canonical — read-only) */}
                    <td className="px-3 py-1.5">
                      <span className="font-mono text-[rgb(var(--color-text))]">{row.target_column_name}</span>
                      {!row.is_required && (
                        <span className="ml-1.5 text-tok-xs text-[rgb(var(--color-text-muted))]">optional</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {saveError && (
        <p className="text-tok-xs text-[rgb(var(--color-danger))]">{saveError}</p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-border))] transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm || saving}
          className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {saving ? "Saving…" : "Confirm mapping"}
        </button>
      </div>
    </div>
  );
}

// ── EntityUploadPanel ─────────────────────────────────────────────────────────
// Four-step post S558: Choose file → Map columns → Validate → Import
// onImportSuccess(json) — called after 200/207, allows parent to react

const STEP = {
  IDLE:      "IDLE",
  FETCHING:  "FETCHING",   // loading saved mapping from server after file select
  MAPPING:   "MAPPING",    // user confirming/editing column mapping
  VALIDATING:"VALIDATING",
  VALIDATED: "VALIDATED",
  UPLOADING: "UPLOADING",
  DONE:      "DONE",
};

function EntityUploadPanel({ fileType, entityLabel, onImportSuccess, isLocked, period }) {
  const fileInputRef = useRef(null);
  const [step,           setStep]           = useState(STEP.IDLE);
  const [file,           setFile]           = useState(null);
  const [rawHeaders,     setRawHeaders]     = useState([]);       // headers from CSV row 0
  const [mappingRows,    setMappingRows]    = useState([]);       // saved mapping from server
  const [confirmedMap,   setConfirmedMap]   = useState([]);       // mapping confirmed by user
  const [missingCols,    setMissingCols]    = useState([]);
  const [optionalAbsent, setOptionalAbsent] = useState([]);
  const [rowCount,       setRowCount]       = useState(null);
  const [result,         setResult]         = useState(null);
  const [historyKey,     setHistoryKey]     = useState(0);
  const [fetchError,     setFetchError]     = useState(null);
  const contract = FIELD_CONTRACTS[fileType] ?? { required: [], optional: [], notes: [] };

  useEffect(() => {
    setStep(STEP.IDLE); setFile(null); setRawHeaders([]); setMappingRows([]);
    setConfirmedMap([]); setMissingCols([]); setOptionalAbsent([]);
    setRowCount(null); setResult(null); setFetchError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [fileType]);

  // After file is chosen: parse headers then fetch saved mapping
  const handleFileChange = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setMissingCols([]);
    setOptionalAbsent([]);
    setRowCount(null);
    setFetchError(null);
    setStep(STEP.FETCHING);
    fetch("/api/v1/addin/telemetry", {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "addin_import_started",
        surface:    "webapp",
        duration_ms: null,
        metadata:   { entity_type: fileType },
      }),
    }).catch(() => {});
    try {
      const { headers, dataRowCount } = await parseCsvFile(f);
      setRawHeaders(headers);
      setRowCount(dataRowCount);
      const saved = await fetchColumnMapping(fileType);
      setMappingRows(saved);
      setStep(STEP.MAPPING);
    } catch (err) {
      setFetchError(err.message ?? "Could not read file.");
      setStep(STEP.IDLE);
    }
  }, [fileType]);

  // User confirmed mapping in ColumnMappingStep
  const handleMappingConfirm = useCallback((confirmed) => {
    setConfirmedMap(confirmed);
    // Apply mapping to raw headers and run validation immediately
    const canonicalHeaders = applyMappingToHeaders(rawHeaders, confirmed);
    const missing = validateHeaders(canonicalHeaders, fileType);
    const absent  = contract.optional.filter((c) => !canonicalHeaders.includes(c));
    setMissingCols(missing);
    setOptionalAbsent(absent);
    setStep(missing.length > 0 ? STEP.IDLE : STEP.VALIDATED);
    // If missing required columns remain after mapping, surface the error clearly
    if (missing.length > 0) {
      // Stay at IDLE so user can re-select file or re-map
    }
    fetch("/api/v1/addin/telemetry", {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "addin_analyze_complete",
        surface:    "webapp",
        duration_ms: null,
        metadata:   { entity_type: fileType, mapped_count: confirmed.length },
      }),
    }).catch(() => {});
  }, [rawHeaders, fileType, contract.optional]);

  const handleMappingCancel = useCallback(() => {
    setStep(STEP.IDLE);
    setFile(null);
    setRawHeaders([]);
    setMappingRows([]);
    setFetchError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleImport = useCallback(async () => {
    if (!file || step !== STEP.VALIDATED) return;
    setStep(STEP.UPLOADING);
    setResult(null);
    fetch("/api/v1/addin/telemetry", {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: "addin_review_confirmed",
        surface:    "webapp",
        duration_ms: null,
        metadata:   { entity_type: fileType, row_count: rowCount },
      }),
    }).catch(() => {});
    const t0Import = Date.now();
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source_system", SOURCE_SYSTEM);
      fd.append("file_type", fileType);
      const resp = await fetch("/api/v1/import/upload", { method: "POST", credentials: "include", body: fd });
      let json = {};
      try { json = await resp.json(); } catch { /* empty */ }
      setResult({ status: resp.status, json });
      setStep(STEP.DONE);
      if (resp.status === 200 || resp.status === 207) {
        fetch("/api/v1/addin/telemetry", {
          method:      "POST",
          credentials: "include",
          headers:     { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "addin_import_completed",
            surface:    "webapp",
            duration_ms: Date.now() - t0Import,
            metadata:   {
              entity_type:   fileType,
              rows_inserted: json?.import_log?.rows_inserted ?? json?.rows_inserted ?? 0,
              rows_rejected: json?.import_log?.rows_rejected ?? json?.rows_rejected ?? 0,
            },
          }),
        }).catch(() => {});
        setHistoryKey((k) => k + 1);
        onImportSuccess?.(json);
      } else {
        fetch("/api/v1/addin/telemetry", {
          method:      "POST",
          credentials: "include",
          headers:     { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "addin_import_failed",
            surface:    "webapp",
            duration_ms: Date.now() - t0Import,
            error_code:  json?.error?.code ?? null,
            metadata:   { entity_type: fileType },
          }),
        }).catch(() => {});
      }
    } catch (err) {
      fetch("/api/v1/addin/telemetry", {
        method:      "POST",
        credentials: "include",
        headers:     { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "addin_import_failed",
          surface:    "webapp",
          duration_ms: Date.now() - t0Import,
          error_code:  "NETWORK_ERROR",
          metadata:   { entity_type: fileType },
        }),
      }).catch(() => {});
      setResult({ status: 0, json: { error: { code: "NETWORK_ERROR", message: err.message ?? "Network error." } } });
      setStep(STEP.DONE);
    }
  }, [file, step, fileType, rowCount, onImportSuccess]);

  const handleReset = useCallback(() => {
    setStep(STEP.IDLE); setFile(null); setRawHeaders([]); setMappingRows([]);
    setConfirmedMap([]); setMissingCols([]); setOptionalAbsent([]);
    setRowCount(null); setResult(null); setFetchError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const isUploading = step === STEP.UPLOADING;
  const validated   = [STEP.VALIDATED, STEP.UPLOADING, STEP.DONE].includes(step);
  const hasErrors   = missingCols.length > 0;

  // Mapping step — full replacement render
  if (step === STEP.MAPPING) {
    return (
      <div className="space-y-4">
        <FieldContractCard fileType={fileType} />
        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-4">
          <ColumnMappingStep
            fileType={fileType}
            rawHeaders={rawHeaders}
            mappingRows={mappingRows}
            onConfirm={handleMappingConfirm}
            onCancel={handleMappingCancel}
          />
        </div>
      </div>
    );
  }

  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern.
  // Replaces entire upload UI with locked banner when period is not open.
  if (isLocked) {
    return (
      <div className="space-y-4">
        <FieldContractCard fileType={fileType} />
        <div className="flex items-center gap-2 rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5">
          <Lock className="h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
          <p className="text-tok-sm text-[rgb(var(--color-warning))]">
            Period is <strong>{period?.period_status ?? "not open"}</strong> — imports are not available for this period.
            Switch to an open period to import data.
          </p>
        </div>
        <ImportHistoryCard fileType={fileType} historyKey={0} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FieldContractCard fileType={fileType} />

      <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-3">
        {/* File picker row */}
        <div className="flex items-center gap-3 flex-wrap">
          <label
            htmlFor={`file-${fileType}`}
            className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-border))] transition-colors"
          >
            Choose file
          </label>
          <input
            ref={fileInputRef}
            id={`file-${fileType}`}
            type="file"
            accept=".csv"
            className="sr-only"
            onChange={handleFileChange}
          />
          <span className="flex-1 text-tok-sm text-[rgb(var(--color-text-muted))] truncate min-w-0">
            {file ? file.name : "No file chosen"}
          </span>
          {file && (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1 text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors shrink-0"
            >
              <RotateCcw className="h-3 w-3" /> Clear
            </button>
          )}
        </div>

        {/* Fetching mapping spinner */}
        {step === STEP.FETCHING && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] flex items-center gap-2">
            <div className="h-3.5 w-3.5 rounded-full border-2 border-[rgb(var(--color-primary))] border-t-transparent animate-spin" />
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Loading column mapping…</p>
          </div>
        )}

        {/* Fetch error */}
        {fetchError && step === STEP.IDLE && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] space-y-1">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">Could not read file</p>
            </div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{fetchError}</p>
          </div>
        )}

        {/* Post-mapping validation errors */}
        {hasErrors && step === STEP.IDLE && missingCols.length > 0 && !fetchError && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] space-y-1.5">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-[rgb(var(--color-danger))] shrink-0" />
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-danger))]">
                Required fields still missing after mapping
              </p>
            </div>
            <p className="font-mono text-tok-xs text-[rgb(var(--color-danger))] leading-relaxed">
              {missingCols.join(", ")}
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Re-select the file and update your column mapping to cover all required fields.
            </p>
          </div>
        )}

        {/* Validated: row count + optional warnings + Import button */}
        {validated && !hasErrors && (
          <div className="pt-1 border-t border-[rgb(var(--color-border))] space-y-2.5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                  Columns mapped
                  {rowCount != null && (
                    <span className="font-normal text-[rgb(var(--color-text-muted))]"> — {fmt(rowCount)} data row{rowCount !== 1 ? "s" : ""}</span>
                  )}
                </p>
              </div>
              {step !== STEP.DONE && (
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={isUploading}
                  className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  {isUploading ? "Importing…" : `Import ${entityLabel}`}
                </button>
              )}
            </div>
            {optionalAbsent.length > 0 && (
              <div className="flex items-start gap-2">
                <Info className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))] shrink-0 mt-0.5" />
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Optional columns not in file: <span className="font-mono">{optionalAbsent.join(", ")}</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {result && <ImportResultCard result={result} />}
      <ImportHistoryCard fileType={fileType} historyKey={historyKey} />
    </div>
  );
}

// ── RT Shells — two-panel (Import + Approve) ──────────────────────────────────
//
// Approve panel:
//   GET  /api/v1/addin/lookups/pending_rate_table_approvals → { pending: [{ rate_table_id, rate_table_code }] }
//   POST /api/v1/rate-tables/{rate_table_id}/approve        (per-shell, webapp-native endpoint)
//   Error: SELF_APPROVAL_PROHIBITED

const RT_PANELS = [{ id: "import", label: "1 · Import" }, { id: "approve", label: "2 · Approve" }];

function RtShellsPanel({ isLocked, period }) {
  const [activePanel,     setActivePanel]     = useState("import");
  const [pendingList,     setPendingList]     = useState([]);
  const [pendingLoading,  setPendingLoading]  = useState(false);
  const [pendingError,    setPendingError]    = useState(null);
  const [selectedIds,     setSelectedIds]     = useState([]);
  const [approving,       setApproving]       = useState(false);
  const [approvalResult,  setApprovalResult]  = useState(null);
  const [approvalError,   setApprovalError]   = useState(null);
  const [justImported,    setJustImported]    = useState(false);

  const loadPending = useCallback(async () => {
    setPendingLoading(true); setPendingError(null);
    try {
      const r = await fetch("/api/v1/addin/lookups/pending_rate_table_approvals", { credentials: "include" });
      const d = await r.json();
      // S568: endpoint returns `items` not `pending` — align with actual response shape
      setPendingList(d.items ?? d.pending ?? []);
    } catch { setPendingError("Could not load pending shells."); }
    finally { setPendingLoading(false); }
  }, []);

  useEffect(() => { if (activePanel === "approve") loadPending(); }, [activePanel, loadPending]);

  // S568: Auto-select all shells on load so CA-2 sees all checked by default.
  // Deselect exceptions before approving. Fixes "Approve 1 shell" label bug
  // caused by selectedIds being empty when pendingList first populates.
  useEffect(() => {
    if (pendingList.length > 0) {
      setSelectedIds(pendingList.map((s) => s.rate_table_id));
    }
  }, [pendingList]);

  const toggleShell = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((s) => s !== id) : [...p, id]);
  const toggleAll   = () => setSelectedIds(selectedIds.length === pendingList.length ? [] : pendingList.map((s) => s.rate_table_id));

  const handleApprove = useCallback(async () => {
    if (!selectedIds.length) return;
    setApproving(true); setApprovalError(null); setApprovalResult(null);
    let approvedCount = 0; let lastError = null;
    for (const id of selectedIds) {
      try {
        const r = await fetch(`/api/v1/rate-tables/${id}/approve`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
        const d = await r.json();
        if (r.ok) { approvedCount++; }
        else {
          const code = d?.detail?.error?.code ?? d?.error?.code ?? d?.error;
          lastError = friendlyApprovalError(code, d?.detail?.error?.message ?? d?.error?.message);
        }
      } catch (err) { lastError = err.message ?? "Approval failed."; }
    }
    setApproving(false);
    if (approvedCount > 0) {
      setApprovalResult({ approved_count: approvedCount });
      setSelectedIds([]);
      loadPending();
    }
    if (lastError) setApprovalError(lastError);
  }, [selectedIds, loadPending]);

  const handleImportSuccess = useCallback(() => {
    setJustImported(true);
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Rate Table Shells</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Import shells, then approve before loading rate bands.</p>
      </div>
      <PanelSwitcher panels={RT_PANELS} activePanel={activePanel} onPanelChange={(p) => { setActivePanel(p); setApprovalResult(null); setApprovalError(null); }} />

      {activePanel === "import" && (
        <>
          <EntityUploadPanel fileType="rate_table_shell" entityLabel="RT Shells" onImportSuccess={handleImportSuccess} isLocked={isLocked} period={period} />
          {justImported && (
            <button
              type="button"
              onClick={() => { setJustImported(false); setActivePanel("approve"); }}
              className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 transition-opacity"
            >
              Approve shells →
            </button>
          )}
        </>
      )}

      {activePanel === "approve" && (
        <div className="space-y-4">
          {approvalResult && (
            <div className="rounded-lg border border-[rgb(var(--color-success))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  {approvalResult.approved_count} shell{approvalResult.approved_count !== 1 ? "s" : ""} approved — rate bands can now be loaded
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Pending shells</p>
            <button type="button" onClick={loadPending} disabled={pendingLoading} className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-50">
              {pendingLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {pendingError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{pendingError}</p>}
          {!pendingLoading && !pendingError && !pendingList.length && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No pending shells awaiting approval.</p>
          )}

          {pendingList.length > 0 && (
            <div className="space-y-2">
              <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="w-full text-left px-3 py-2 text-tok-xs text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]"
                >
                  {selectedIds.length === pendingList.length ? "Deselect all" : "Select all"}
                </button>
                {pendingList.map((shell) => (
                  // S568: Use div+onClick instead of label to prevent double-fire.
                  // label wrapping an input fires onChange twice on row click.
                  <div key={shell.rate_table_id} onClick={() => toggleShell(shell.rate_table_id)} className="flex items-center gap-2.5 px-3 py-2 hover:bg-[rgb(var(--color-surface-muted))] cursor-pointer border-b border-[rgb(var(--color-border))] last:border-0">
                    <input type="checkbox" checked={selectedIds.includes(shell.rate_table_id)} onChange={() => {}} className="shrink-0 pointer-events-none" />
                    <span className="font-mono text-tok-xs text-[rgb(var(--color-text))]">{shell.rate_table_code}</span>
                  </div>
                ))}
              </div>

              {approvalError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{approvalError}</p>}

              <button
                type="button"
                onClick={handleApprove}
                disabled={!selectedIds.length || approving}
                className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              >
                {approving ? "Approving…" : `Approve ${selectedIds.length > 0 ? selectedIds.length + " " : ""}shell${selectedIds.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Plans — two-panel (Upload + Approve) ─────────────────────────────────────
//
// Upload panel: after success, plan_import_session_id displayed for Actor 1 to share.
// Approve panel:
//   GET  /api/v1/addin/lookups/pending_plan_approvals → { pending: [{ plan_import_session_id, plan_count, first_plan_code }] }
//   POST /api/v1/plans/import/approve                 → { plans_approved, approved_at, approved_by }
//   Errors: SELF_APPROVAL_PROHIBITED | PLAN_ALREADY_APPROVED | PLAN_SESSION_NOT_FOUND

const PLAN_PANELS = [{ id: "upload", label: "1 · Upload" }, { id: "approve", label: "2 · Approve" }];

function PlansPanel({ isLocked, period }) {
  const [activePanel,      setActivePanel]      = useState("upload");
  const [sessionId,        setSessionId]        = useState(null);
  const [pendingList,      setPendingList]      = useState([]);
  const [pendingLoading,   setPendingLoading]   = useState(false);
  const [pendingError,     setPendingError]     = useState(null);
  const [sessionIdInput,   setSessionIdInput]   = useState("");
  const [selectedSession,  setSelectedSession]  = useState(null);
  const [approving,        setApproving]        = useState(false);
  const [approvalResult,   setApprovalResult]   = useState(null);
  const [approvalError,    setApprovalError]    = useState(null);

  const loadPending = useCallback(async () => {
    setPendingLoading(true); setPendingError(null);
    try {
      const r = await fetch("/api/v1/addin/lookups/pending_plan_approvals", { credentials: "include" });
      const d = await r.json();
      // S568: endpoint returns `items` not `pending` — align with actual response shape
      setPendingList(d.items ?? d.pending ?? []);
    } catch { setPendingError("Could not load pending plan batches."); }
    finally { setPendingLoading(false); }
  }, []);

  useEffect(() => { if (activePanel === "approve") loadPending(); }, [activePanel, loadPending]);

  const handleImportSuccess = useCallback((json) => {
    const sid = json?.import_log?.plan_import_session_id ?? json?.plan_import_session_id ?? null;
    setSessionId(sid);
  }, []);

  const handleSelectSession = (sid, planCount, firstPlanCode) => {
    setSelectedSession({ sessionId: sid, planCount, firstPlanCode });
    setSessionIdInput(sid);
    setApprovalError(null);
  };

  const handleSessionIdSubmit = () => {
    const t = sessionIdInput.trim();
    if (!t) return;
    const match = pendingList.find((p) => p.plan_import_session_id === t);
    setSelectedSession({ sessionId: t, planCount: match?.plan_count ?? null, firstPlanCode: match?.first_plan_code ?? null });
    setApprovalError(null);
  };

  const handleApprove = useCallback(async () => {
    if (!selectedSession?.sessionId) return;
    setApproving(true); setApprovalError(null); setApprovalResult(null);
    try {
      const r = await fetch("/api/v1/plans/import/approve", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_import_session_id: selectedSession.sessionId }),
      });
      const d = await r.json();
      if (r.ok) {
        setApprovalResult(d);
        setSelectedSession(null);
        setSessionIdInput("");
        loadPending();
      } else {
        const code = d?.detail?.error?.code ?? d?.error?.code ?? d?.error;
        setApprovalError(friendlyApprovalError(code, d?.detail?.error?.message ?? d?.error?.message));
      }
    } catch (err) { setApprovalError(err.message ?? "Approval failed."); }
    finally { setApproving(false); }
  }, [selectedSession, loadPending]);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plans</p>
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">Actor 1 imports plans in draft · Actor 2 approves.</p>
      </div>
      <PanelSwitcher panels={PLAN_PANELS} activePanel={activePanel} onPanelChange={(p) => { setActivePanel(p); setApprovalResult(null); setApprovalError(null); }} />

      {activePanel === "upload" && (
        <>
          <EntityUploadPanel fileType="plan" entityLabel="Plans" onImportSuccess={handleImportSuccess} isLocked={isLocked} period={period} />
          {sessionId && (
            <div className="rounded-lg border border-[rgb(var(--color-primary))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-2">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Plans created in draft — awaiting approval</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Share this session ID with the approving comp admin:</p>
              <p className="font-mono text-tok-xs text-[rgb(var(--color-primary))] break-all select-all">{sessionId}</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">The approver opens the <strong>2 · Approve</strong> tab and enters this ID.</p>
              <button
                type="button"
                onClick={() => setActivePanel("approve")}
                className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 transition-opacity"
              >
                Approve plans →
              </button>
            </div>
          )}
        </>
      )}

      {activePanel === "approve" && (
        <div className="space-y-4">
          {approvalResult && (
            <div className="rounded-lg border border-[rgb(var(--color-success))] bg-[rgb(var(--color-surface))] px-4 py-3 space-y-1.5">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))] shrink-0" />
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-success))]">
                  {approvalResult.plans_approved ?? 0} plan{(approvalResult.plans_approved ?? 0) !== 1 ? "s" : ""} approved — plan assignments can now be loaded
                </p>
              </div>
              {approvalResult.approved_at && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Approved at {new Date(approvalResult.approved_at).toLocaleString()}</p>}
              {approvalResult.approved_by && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Approved by <span className="font-mono">{approvalResult.approved_by}</span></p>}
              <button type="button" onClick={() => { setApprovalResult(null); loadPending(); }} className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline">
                Approve another batch
              </button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Pending approvals</p>
            <button type="button" onClick={loadPending} disabled={pendingLoading} className="text-tok-xs text-[rgb(var(--color-primary))] hover:underline disabled:opacity-50">
              {pendingLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {pendingError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{pendingError}</p>}
          {!pendingLoading && !pendingError && !pendingList.length && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No pending plan batches.</p>
          )}

          {pendingList.length > 0 && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] overflow-hidden max-h-36 overflow-y-auto">
              {pendingList.map((item) => {
                const isSel = selectedSession?.sessionId === item.plan_import_session_id;
                return (
                  <button
                    key={item.plan_import_session_id}
                    type="button"
                    onClick={() => handleSelectSession(item.plan_import_session_id, item.plan_count, item.first_plan_code)}
                    className={[
                      "w-full flex flex-col px-3 py-2 text-left transition-colors border-b border-[rgb(var(--color-border))] last:border-0",
                      isSel ? "bg-[rgb(var(--color-primary))] bg-opacity-10 border-l-2 border-l-[rgb(var(--color-primary))]" : "hover:bg-[rgb(var(--color-surface-muted))]",
                    ].join(" ")}
                  >
                    <span className="font-mono text-tok-xs text-[rgb(var(--color-text))] truncate">{item.plan_import_session_id}</span>
                    <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{item.plan_count} plan{item.plan_count !== 1 ? "s" : ""} · first: {item.first_plan_code}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-tok-xs font-medium text-[rgb(var(--color-text))]">Or enter session ID manually</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={sessionIdInput}
                onChange={(e) => setSessionIdInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSessionIdSubmit()}
                placeholder="plan_import_session_id (UUID)"
                className="flex-1 font-mono text-tok-xs rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--color-primary))]"
              />
              <button
                type="button"
                onClick={handleSessionIdSubmit}
                disabled={!sessionIdInput.trim()}
                className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-border))] disabled:opacity-50 transition-colors"
              >
                Load
              </button>
            </div>
          </div>

          {selectedSession && (
            <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 space-y-1">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] uppercase tracking-wide">Batch to approve</p>
              <p className="font-mono text-tok-xs text-[rgb(var(--color-text))] break-all">{selectedSession.sessionId}</p>
              {selectedSession.planCount != null && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{selectedSession.planCount} plan{selectedSession.planCount !== 1 ? "s" : ""}</p>}
              {selectedSession.firstPlanCode && <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">First plan: <span className="font-mono text-[rgb(var(--color-text))]">{selectedSession.firstPlanCode}</span></p>}
            </div>
          )}

          {approvalError && <p className="text-tok-xs text-[rgb(var(--color-danger))]">{approvalError}</p>}

          {selectedSession && (
            <button
              type="button"
              onClick={handleApprove}
              disabled={approving}
              className="inline-flex items-center gap-2 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {approving ? "Approving…" : "Approve all plans"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Ramp Schedules — upload panel with mixed-effective-date warning surface ───
//
// Wraps EntityUploadPanel. On a successful 200 response, checks result.error_summary
// and surfaces it as a non-blocking amber warning banner (data was written).
// error_summary is populated by §87.8 when the same role_code appears with more
// than one distinct effective_date in the batch (S557 D1).

function RampSchedulePanel({ isLocked, period }) {
  const [warningMessage, setWarningMessage] = useState(null);

  const handleImportSuccess = useCallback((json) => {
    setWarningMessage(json?.error_summary ?? null);
  }, []);

  return (
    <div className="space-y-4">
      <EntityUploadPanel
        fileType="ramp_schedule"
        entityLabel="Ramp Schedules"
        onImportSuccess={handleImportSuccess}
        isLocked={isLocked}
        period={period}
      />
      {warningMessage && (
        <div className="rounded-lg border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface))] px-4 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-[rgb(var(--color-warning))] shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Import warning — data was written</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">{warningMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Import Log tab ────────────────────────────────────────────────────────────

function ImportLogTab() {
  return <ImportLogViewer aiStatus="idle" aiResponse={null} currentScreen="ImportLogViewer" onNavigate={() => {}} />;
}

// ── ImportDataPanel root ──────────────────────────────────────────────────────

export { EntityUploadPanel };

export default function ImportDataPanel({ period }) {
  const [activeTab,             setActiveTab]             = useState("positions");
  const [activeRateTableSubTab, setActiveRateTableSubTab] = useState("rate_table_shell");
  const [activePlansSubTab,     setActivePlansSubTab]     = useState("plan");
  const [guidedTooltip,         setGuidedTooltip]         = useState(false);

  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly.
  // Gates all file upload UI when period is not open (locked or closed).
  const isLocked = period?.period_status !== "open";

  return (
    <div className="space-y-0">
      {/* Panel header */}
      <div className="flex items-start justify-between pb-4">
        <div>
          <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Import Data</h2>
          {period && (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              Active period:{" "}
              <span className="font-medium text-[rgb(var(--color-text))]">{period.period_label ?? period.period_code}</span>
              {" "}· SETUP imports are tenant-level — delta imports run inside each EXECUTE panel
            </p>
          )}
        </div>
        {/* Guide me stub — OI-WEBAPP-AGENT-IMPORT */}
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={() => setGuidedTooltip((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5" /> Guide me +
          </button>
          {guidedTooltip && (
            <div className="absolute right-0 top-full mt-2 z-20 w-64 rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-3 shadow-lg space-y-1.5">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-text))]">AI-guided import</p>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))] leading-relaxed">
                Agent-assisted import is coming to the webapp. Use the Excel Add-in today for guided import with column mapping and pre-flight analysis.
              </p>
              <p className="font-mono text-[10px] text-[rgb(var(--color-text-muted))]">OI-WEBAPP-AGENT-IMPORT</p>
              <button type="button" onClick={() => setGuidedTooltip(false)} className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors">Dismiss</button>
            </div>
          )}
        </div>
      </div>

      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="pt-5">
        {activeTab === "positions" && (
          <EntityUploadPanel fileType="position" entityLabel="Positions" isLocked={isLocked} period={period} />
        )}
        {activeTab === "ote_history" && (
          <EntityUploadPanel fileType="ote_history" entityLabel="OTE History" isLocked={isLocked} period={period} />
        )}
        {activeTab === "rate_tables" && (
          <>
            <SubTabBar subTabs={TABS.find((t) => t.id === "rate_tables").subTabs} activeSubTab={activeRateTableSubTab} onSubTabChange={setActiveRateTableSubTab} />
            {activeRateTableSubTab === "rate_table_shell" && <RtShellsPanel isLocked={isLocked} period={period} />}
            {activeRateTableSubTab === "rate_table_row"   && <EntityUploadPanel fileType="rate_table_row" entityLabel="Rate Table Rows" isLocked={isLocked} period={period} />}
          </>
        )}
        {activeTab === "ramp_schedules" && (
          <RampSchedulePanel isLocked={isLocked} period={period} />
        )}
        {activeTab === "plans_group" && (
          <>
            <SubTabBar subTabs={TABS.find((t) => t.id === "plans_group").subTabs} activeSubTab={activePlansSubTab} onSubTabChange={setActivePlansSubTab} />
            {activePlansSubTab === "plan"            && <PlansPanel isLocked={isLocked} period={period} />}
            {activePlansSubTab === "plan_assignment" && <EntityUploadPanel fileType="plan_assignment" entityLabel="Plan Assignments" isLocked={isLocked} period={period} />}
          </>
        )}
        {activeTab === "quotas" && (
          <EntityUploadPanel fileType="quota" entityLabel="Quotas" isLocked={isLocked} period={period} />
        )}
        {activeTab === "credits" && (
          <EntityUploadPanel fileType="credit" entityLabel="Credits" isLocked={isLocked} period={period} />
        )}
        {activeTab === "aop_inputs" && (
          <EntityUploadPanel fileType="aop_input" entityLabel="AOP Inputs" isLocked={isLocked} period={period} />
        )}
        {activeTab === "import_log" && <ImportLogTab />}
      </div>
    </div>
  );
}
